/**
 * Offline queue — field actions (punches, safety confirm, rip-outs, expenses,
 * health claims) are stored in IndexedDB when there's no signal (or the
 * request can't reach the server) and replayed in order once we're back
 * online. Each queued body carries `client_at` = when the user really tapped.
 */
import api from "@/lib/api";

const DB = "elite-offline", STORE = "queue";
const listeners = new Set();
let flushing = false;

const open = () => new Promise((res, rej) => {
  const r = indexedDB.open(DB, 1);
  r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "id" });
  r.onsuccess = () => res(r.result);
  r.onerror = () => rej(r.error);
});
const tx = async (mode, fn) => {
  const db = await open();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, mode);
    const out = fn(t.objectStore(STORE));
    t.oncomplete = () => { db.close(); res(out && "result" in out ? out.result : out); };
    t.onerror = () => { db.close(); rej(t.error); };
  });
};
const all = () => tx("readonly", (s) => s.getAll());
const notify = async () => { const items = await all().catch(() => []); listeners.forEach((fn) => fn(items)); };

export const subscribe = (fn) => { listeners.add(fn); all().then(fn).catch(() => {}); return () => listeners.delete(fn); };
export const pending = () => all().catch(() => []);
export const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;
const isNetworkError = (e) => !e?.response && (e?.code === "ERR_NETWORK" || e?.message === "Network Error" || isOffline());

const serialize = (data) => {
  if (data instanceof FormData) {
    const fields = [];
    data.forEach((v, k) => fields.push(v instanceof Blob ? { name: k, blob: v, filename: v.name || "file" } : { name: k, value: String(v) }));
    return { kind: "form", fields };
  }
  return { kind: "json", body: data || {} };
};
const rebuild = (item) => {
  if (item.kind === "json") return { ...item.body, client_at: item.createdAt };
  const fd = new FormData();
  item.fields.forEach((f) => (f.blob ? fd.append(f.name, f.blob, f.filename) : fd.append(f.name, f.value)));
  fd.append("client_at", item.createdAt);
  return fd;
};

/** POST now, or queue it when offline / unreachable. Resolves {queued:true,label} or the axios response. */
export async function postOrQueue(url, data, label = "action") {
  const item = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, url, label, createdAt: new Date().toISOString(), ...serialize(data) };
  if (!isOffline()) {
    try {
      return await api.post(url, rebuild(item));
    } catch (e) {
      if (!isNetworkError(e)) throw e;
    }
  }
  await tx("readwrite", (s) => s.put(item));
  notify();
  return { queued: true, label };
}

/** Replay everything in order; stops at the first item that can't reach the server. */
export async function flush() {
  if (flushing || isOffline()) return { sent: 0, failed: 0 };
  flushing = true;
  let sent = 0, failed = 0;
  try {
    const items = (await all()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const item of items) {
      try {
        await api.post(item.url, rebuild(item));
        await tx("readwrite", (s) => s.delete(item.id));
        sent += 1;
      } catch (e) {
        if (isNetworkError(e)) break;                       // still no signal — try again later
        // the server rejected it (e.g. already clocked in): drop it so the queue doesn't jam, but remember why
        await tx("readwrite", (s) => s.delete(item.id));
        failed += 1;
        rejected.push({ ...item, error: e?.response?.data?.detail || "Rejected by the server" });
      }
    }
  } finally {
    flushing = false;
    notify();
  }
  return { sent, failed };
}
export const rejected = [];
export const dropRejected = () => { rejected.length = 0; };

if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flush(); });
  setInterval(() => { if (!isOffline()) pending().then((p) => p.length && flush()); }, 30000);
}
